import React, { useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../shared/contexts/AuthContext';
import { PermissionProvider } from '../shared/contexts/PermissionContext';
import { AuthGuard } from '../shared/guards/AuthGuard';
import { NoAuthGuard } from '../shared/guards/NoAuthGuard';
import { TokenManager } from '../shared/utils/token-manager';
import Layout from '../shared/components/Layout';
import { SignIn } from '../features/auth/components/SignIn';
import { SignUp } from '../features/auth/components/SignUp';
import { Dashboard } from '../features/dashboard/components/Dashboard';
import { Settings } from '../features/settings/components/Settings';
import { Error404 } from '../features/errors/components/Error404';
import { Error500 } from '../features/errors/components/Error500';
import { Loader } from '../shared/components/Loader';
import { AddEditAdmin } from '../features/admin/components/AddEditAdmin';
import { Admin } from '../features/admin/components/Admin';
import { Profile } from '../features/profile/components/Profile';
import { ChangePassword } from '../features/profile/components/ChangePassword';
import { Clients } from '../features/clients/components/Clients';
import { AddEditClient } from '../features/clients/components/AddEditClient';
import { ViewClient } from '../features/clients/components/ViewClient';
import { Accounts } from '../features/accounts/components/Accounts';
import { AddEditAccount } from '../features/accounts/components/AddEditAccount';
import { Invoices } from '../features/invoices/components/Invoices';
import { AddEditInvoice } from '../features/invoices/components/AddEditInvoice';
import { ViewInvoice } from '../features/invoices/components/ViewInvoice';
import { PartialPaymentInvoice } from '../features/invoices/components/PartialPaymentInvoice';
import { TDSList } from '../features/tds/components/TDSList';
import { LossList } from '../features/losses/components/LossList';
import { SalesList } from '../features/invoices/components/SalesList';
import { GstReport } from '../features/invoices/components/GstReport';
import { Reminders } from '../features/reminders/components/Reminders';
import { Services } from '../features/invoices/components/Services';
import { useReminderNotifications } from '../shared/hooks/useReminderNotifications';


const AppContent: React.FC = () => {
    const { handleToken, check, isInitializing, authenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    useReminderNotifications(authenticated, navigate);

    useEffect(() => {
        const processToken = async () => {
            const urlParams = new URLSearchParams(location.search);
            let token = urlParams.get('token');

            if (!token && location.hash) {
                const hashParams = new URLSearchParams(location.hash.split('?')[1] || '');
                token = hashParams.get('token');
            }

            if (token) {
                try {
                    await handleToken(token);
                    TokenManager.cleanupTokenFromUrl();
                    const isAuth = await check();
                    if (isAuth) {
                        // Avoid full page reload, just navigate
                        if (location.pathname === '/sign-in' || location.pathname === '/') {
                            navigate('/dashboard', { replace: true });
                        }
                    } else {
                        navigate('/sign-in', { replace: true });
                    }
                } catch (error) {
                    console.error('Error processing token:', error);
                    TokenManager.cleanupTokenFromUrl();
                    navigate('/sign-in', { replace: true });
                }
            } else {
                check();
            }
        };

        processToken();
    }, [location.search, location.hash, handleToken, check, navigate]);

    if (isInitializing) {
        return <Loader fullScreen={true} />;
    }

    return (
        <Routes>
            {/* Redirect empty path based on auth status */}
            <Route path="/" element={
                authenticated ? <Navigate to="/dashboard" replace /> : <Navigate to="/sign-in" replace />
            } />
            <Route path="/signed-in-redirect" element={<Navigate to="/dashboard" replace />} />

            {/* Auth routes for guests */}
            <Route element={<NoAuthGuard><Layout layout="empty" /></NoAuthGuard>}>
                <Route path="/sign-in" element={<SignIn />} />
                <Route path="/sign-up" element={<SignUp />} />
            </Route>

            {/* ADMIN ROUTES */}
            <Route element={<AuthGuard><Layout layout="classic" /></AuthGuard>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/add" element={<AddEditAdmin />} />
                <Route path="/admin/edit/:id" element={<AddEditAdmin />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/clients/add" element={<AddEditClient />} />
                <Route path="/clients/edit/:id" element={<AddEditClient />} />
                <Route path="/clients/view/:id" element={<ViewClient />} />
                <Route path="/accounts" element={<Accounts />} />
                <Route path="/accounts/add" element={<AddEditAccount />} />
                <Route path="/accounts/edit/:id" element={<AddEditAccount />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/invoices/add" element={<AddEditInvoice />} />
                <Route path="/invoices/edit/:id" element={<AddEditInvoice />} />
                <Route path="/invoices/view/:id" element={<ViewInvoice />} />
                <Route path="/invoices/partial-payment/:id" element={<PartialPaymentInvoice />} />
                <Route path="/invoices/sales" element={<SalesList />} />
                <Route path="/invoices/services" element={<Services />} />
                <Route path="/invoices/gst-report" element={<GstReport />} />
                <Route path="/reminders" element={<Reminders />} />
                <Route path="/tds" element={<TDSList />} />
                <Route path="/losses" element={<LossList />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/logout" element={<Navigate to="/sign-in" replace />} />
            </Route>

            {/* Error pages */}
            <Route path="/404-not-found" element={<Error404 />} />
            <Route path="/500" element={<Error500 />} />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/404-not-found" replace />} />
        </Routes>
    );
};

const App: React.FC = () => {
    return (
        <HashRouter>
            <AuthProvider>
                <PermissionProvider>
                    <AppContent />
                </PermissionProvider>
            </AuthProvider>
        </HashRouter>
    );
};

export default App;
